/* eslint-disable max-len */
import React, { useState, useEffect, useContext } from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'babel-plugin-relay/macro';
import { Container, Row, Col } from 'react-bootstrap';
import Slider from 'react-slick';

import usePageTitle from '../../hooks/usePageTitle';

import HeaderThemeContext from '../../context/HeaderThemeContext';

import type { Home_query$key } from './__generated__/Home_query.graphql';
import type { Home_query_speakers$key } from './__generated__/Home_query_speakers.graphql';
import type { Home_query_partners$key } from './__generated__/Home_query_partners.graphql';
import { transformArrToListMarkup } from '../../utils/helpers';
import { DICTIONARIES } from '../../utils/constants';
import './Home.sass';

import LinkButton from '../../uikit/Links/LinkButton';
import Accordion from '../../uikit/Accordion';
import Promo from '../../uikit/Cards/Promo';
import Person from '../../uikit/Cards/Person';

import HeroBanner from '../../components/HeroBanner';
import SiteDescription from '../../components/SiteDescription';
import Partners from '../../components/Partners';
import Donate from '../../components/Donate';
import { Speakers, Themes } from '../../types';

interface Props {
  query: Home_query$key | null;
  speakers: Home_query_speakers$key | null;
  partners: Home_query_partners$key | null;
  themes: Themes;
  speaker: Speakers;
  labels: {
    [keys: string]: string;
  };
}

interface IThemesState {
  title: string;
  body: string;
}

function Home(props: Props) {
  const [themesContent, setThemesContent] = useState<Themes>();
  const [, setHeaderTheme] = useContext(HeaderThemeContext);
  const [extraSpeakers, setExtraSpeakers] = useState<Speakers>([]);
  const [isBigView, setIsBigView] = useState(true);

  useEffect(() => {
    const tempArray: IThemesState[] = [];

    props.themes.map((item, index) => {
      if (item) {
        const { title, technologies } = item;
        if (title && technologies) {
          tempArray[index] = {
            title,
            body: transformArrToListMarkup(technologies as any),
          };
        }
      }
    });
    setThemesContent(tempArray);
  }, [props.themes]);

  useEffect(() => {
    setHeaderTheme(true);

    return () => setHeaderTheme(false);
  }, []);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = useFragment(
    graphql`
			fragment Home_query on HomePublishedContent {
				metaTitle
				stopLookingForSpeakers
				becomeAPartner
				hideTopics
				...HeroBanner
				...SiteDescription
				...Partners
				...Donate
			}
		`,
    props.query
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const speakersData: any = useFragment(
    graphql`
			fragment Home_query_speakers on PublishedContentByTypeQuery {
				speakers {
					items {
						... on SpeakersPublishedContent {
							_children {
								items {
									... on SpeakerPublishedContent {
										_id
										_url
										_name
										speakerName
										speakerPreviewDescription
										speakerImage
										location {
											... on LocationPublishedContent {
												city
												countryName
												country {
													... on FilePublishedMedia {
														_url
													}
												}
											}
										}
										facebookLink
										twitterLink
										linkedinLink
									}
								}
							}
						}
					}
				}
			}
		`,
    props.speakers
  );

  const allSpeakers = speakersData?.speakers.items[0]._children.items;
  const speakersQuantity = !data.stopLookingForSpeakers ? 7 : 8;

  useEffect(() => {
    const { speaker } = props;
    if (speaker) {
      if (speaker.length < speakersQuantity) {
        const speakersNames = speaker.map((speakerItem) => speakerItem?._name);

        const { items } = speakersData.speakers.items[0]._children;
        if (items && items.length !== 0) {
          const notDuplicatedSpeakers = items.filter(
            (speakeItem) => !speakersNames.includes(speakeItem._name)
          );
          setExtraSpeakers(
            notDuplicatedSpeakers.slice(0, speakersQuantity - speaker.length)
          );
        }
      }
    } else {
      setExtraSpeakers(
        speakersData && allSpeakers.length !== 0
          ? allSpeakers.slice(0, speakersQuantity)
          : []
      );
    }
  }, [props.speaker, speakersData]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const partnersData: any = useFragment(
    graphql`
			fragment Home_query_partners on PublishedContentByTypeQuery {
				partners {
					items {
						... on PartnersPublishedContent {
							_children(orderBy: _updateDate_DESC) {
								items {
									... on PartnerPublishedContent {
										partnerSiteUrl
										picture
										_name
										_id
									}
								}
							}
						}
					}
				}
			}
		`,
    props.partners
  );
  usePageTitle(data?.metaTitle);
  const settings = {
    dots: false,
    nextArrow: <SampleNextArrow/>,
    prevArrow: <SamplePrevArrow/>,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    swipe: true,

    responsive: [
      {
        breakpoint: 1199,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 800,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const mediaQuery = window.matchMedia('(max-width: 767px)');

  function matchMediaQuery(x: { matches: boolean; media: string }) {
    setIsBigView(!x.matches);
  }

  useEffect(() => {
    matchMediaQuery(mediaQuery);
    mediaQuery.addListener(matchMediaQuery);
    return () => {
      mediaQuery.removeListener(matchMediaQuery);
    };
  }, [mediaQuery]);

  return (
    <div className="home-page">
      <HeroBanner
        bannerData={data}
        registerButtonLabel={props.labels[DICTIONARIES.registerButton]}
      />

      <section className="common-section common-section-mt0 home-page-description">
        <Container>
          <Row>
            <Col xs={12} lg={10} xl={8}>
              <SiteDescription descriptionData={data}/>
            </Col>
          </Row>
        </Container>
      </section>
      {data?.hideTopics && themesContent && (
        <section className="common-section home-page-themes">
          <Container>
            <Row>
              <Col xs={12}>
                <Accordion
                  isTinyTitle={false}
                  withGaps
                  content={themesContent}
                />
              </Col>
              <Col xs={12}/>

            </Row>
          </Container>
        </section>
      )}

      <section className="common-section home-page-speakers">
        <Container>
          <Row>
            <Col xs={12} className="home-page-speakers__heading">
              <h2 className="uikit-label-small-caps">
                {props.labels[DICTIONARIES.speakersTitle]}
              </h2>
            </Col>
          </Row>
          <Row>
            {!isBigView || allSpeakers.length < 7 ? (
              <Col xs={12}>
                <Slider {...settings} className="home-page-speakers__carousel">
                  {!data.stopLookingForSpeakers && (
                    <div className="home-page-speakers__slide">
                      <Promo
                        buttonId="speaker_btn"
                        isButtonOrange
                        buttonText={props.labels[DICTIONARIES.speakersButton]}
                        title={props.labels[DICTIONARIES.promoTitle]}
                        text={props.labels[DICTIONARIES.promoText]}
                        link="/become-a-speaker"
                      />
                    </div>
                  )}
                  {props?.speaker?.map((speakerItem: any, index: number) => {
                    if (index < speakersQuantity) {
                      return (
                        <div
                          className="home-page-speakers__slide"
                          key={speakerItem._id}
                        >
                          <Person data={speakerItem}/>
                        </div>
                      );
                    }
                    return null;
                  })}
                  {extraSpeakers?.length !== 0 &&
                    extraSpeakers?.map(
                      (speakerItem) =>
                        speakerItem && (
                          <div
                            className="home-page-speakers__slide"
                            key={speakerItem._id}
                          >
                            <Person data={speakerItem}/>
                          </div>
                        )
                    )}
                </Slider>
              </Col>
            ) : (
              <Row className="speakers-page__list">
                {!data.stopLookingForSpeakers && (
                  <Col xs={12} md={4} lg={3} className="speakers-page__item">
                    <Promo
                      buttonId="speaker_btn"
                      isButtonOrange
                      buttonText={props.labels[DICTIONARIES.speakersButton]}
                      title={props.labels[DICTIONARIES.promoTitle]}
                      text={props.labels[DICTIONARIES.promoText]}
                      link="/become-a-speaker"
                    />
                  </Col>
                )}
                {props?.speaker?.map((speaker, index: number) => {
                  if (index < speakersQuantity && speaker) {
                    return (
                      <Col
                        xs={12}
                        md={4}
                        lg={3}
                        key={speaker._id}
                        className="speakers-page__item"
                      >
                        <Person data={speaker}/>
                      </Col>
                    );
                  }
                  return null;
                })}
                {extraSpeakers?.length !== 0 &&
                  extraSpeakers?.map(
                    (speaker) =>
                      speaker && (
                        <Col
                          xs={12}
                          md={4}
                          lg={3}
                          key={speaker._id}
                          className="speakers-page__item"
                        >
                          <Person data={speaker}/>
                        </Col>
                      )
                  )}
              </Row>
            )}
          </Row>
          {allSpeakers.length >= 7 ? (
            <Row>
              <Col xs={12} className="home-page-partners__btns">
                <LinkButton
                  hrefSource="/speakers"
                  label={props.labels[DICTIONARIES.allSpeakersButton]}
                  isExternal={false}
                />
              </Col>
            </Row>
          ) : null}
        </Container>
      </section>

      <section className="common-section home-page-partners">
        {data && (
          <Partners
            allPartnersList={partnersData}
            partnersList={data}
            partnersTitle={props.labels[DICTIONARIES.partnersTitle]}
          />
        )}

        <Container>
          <Row>
            <Col xs={12} className="home-page-partners__btns">
              {!data.becomeAPartner && (
                <LinkButton
                  id="partner_btn"
                  hrefSource="/become-a-partner"
                  label={props.labels[DICTIONARIES.partnersButton]}
                  isOrange
                  isExternal={false}
                />
              )}

              <LinkButton
                hrefSource="/partners"
                label={props.labels[DICTIONARIES.allPartnersButton]}
                isExternal={false}
              />
            </Col>
          </Row>
        </Container>
      </section>

      <Donate
        donateData={data}
        label={props.labels[DICTIONARIES.donateButton]}
      />
    </div>
  );
}

/* @ts-ignore */
function SampleNextArrow(props) {
  const { className, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-label="Next speaker"
    >
      <svg width="12" height="12" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(-60)">
          <path fill="#000" fillRule="nonzero" d="M60 0h6l6 6-6 6h-6l6-6z"/>
        </g>
      </svg>
    </button>
  );
}

/* @ts-ignore */
function SamplePrevArrow(props) {
  const { className, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-label="Previous speaker"
    >
      <svg width="12" height="12" xmlns="http://www.w3.org/2000/svg">
        <path fill="#000" fillRule="nonzero" d="M12 12H6L0 6l6-6h6L6 6z"/>
      </svg>
    </button>
  );
}

export default Home;
